import { useId, type ReactNode } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatBRL, formatDecimal, formatInt, formatPct, formatTrendLabel, formatTrendRange, type Granularity } from './format';
import { InfoTip } from './InfoTip';
import { METRIC_INFO, type MetricInfoKey } from './metricInfo';
import styles from './TrendChart.module.css';

export type TrendUnit = 'brl' | 'int' | 'dec' | 'pct';

export interface TrendMetric<K extends string> {
    key: K;
    label: string;
    unit: TrendUnit;
    /** Metricas que podem ser negativas deixam o eixo livre; as demais comecam em zero. */
    allowNegative?: boolean;
}

interface TrendPointBase {
    label: string;
}

interface TrendChartProps<K extends string, P extends TrendPointBase> {
    title: string;
    infoKey: MetricInfoKey;
    points: P[];
    granularity: Granularity;
    endDay: string;
    metrics: TrendMetric<K>[];
    metric: K;
    onMetricChange: (metric: K) => void;
    /** Le o valor da metrica no ponto. Nulo vira lacuna na linha. */
    getValue: (point: P, metric: K) => number | null;
    loading?: boolean;
    /** Controles extras antes do seletor de metrica (ex.: evento do grafico). */
    controls?: ReactNode;
}

const FORMATTERS: Record<TrendUnit, (v: number | null | undefined) => string> = {
    brl: formatBRL,
    int: formatInt,
    dec: formatDecimal,
    pct: formatPct,
};

/** Eixo Y compacto: "R$ 1.000" ocupa pouco e nao quebra em duas linhas. */
function axisFormatter(unit: TrendUnit): (v: number) => string {
    if (unit === 'brl') return (v) => `R$ ${formatInt(v)}`;
    return (v) => FORMATTERS[unit](v);
}

export function TrendChart<K extends string, P extends TrendPointBase>({
    title,
    infoKey,
    points,
    granularity,
    endDay,
    metrics,
    metric,
    onMetricChange,
    getValue,
    loading = false,
    controls,
}: TrendChartProps<K, P>) {
    const selectId = useId();
    const current = metrics.find((m) => m.key === metric) ?? metrics[0];
    const data = points.map((p) => ({
        label: p.label,
        axisLabel: formatTrendLabel(p.label, granularity),
        value: getValue(p, current.key),
    }));
    const hasData = data.some((d) => d.value !== null);
    const format = FORMATTERS[current.unit];

    return (
        <section className={`${styles.card} ${loading ? styles.loading : ''}`} aria-busy={loading}>
            <header className={styles.header}>
                <div className={styles.heading}>
                    <h4 className={styles.title}>
                        {title}
                        <InfoTip info={METRIC_INFO[infoKey]} label={title} align="start" />
                    </h4>
                    <p className={styles.subtitle}>{formatTrendRange(points.length, granularity, endDay)}</p>
                </div>
                <div className={styles.controls}>
                    {controls}
                    <div className={styles.metricField}>
                        <label htmlFor={selectId} className={styles.metricLabel}>
                            Métrica
                        </label>
                        <select
                            id={selectId}
                            className={styles.metricSelect}
                            value={current.key}
                            onChange={(e) => onMetricChange(e.target.value as K)}
                        >
                            {metrics.map((m) => (
                                <option key={m.key} value={m.key}>
                                    {m.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </header>

            <div className={styles.chart} role="img" aria-label={`${title}: ${current.label}`}>
                {hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
                            <CartesianGrid vertical={false} stroke="var(--color-gray-200)" />
                            <XAxis
                                dataKey="axisLabel"
                                tickLine={false}
                                axisLine={false}
                                interval="preserveStartEnd"
                                minTickGap={16}
                                tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                width={76}
                                domain={current.allowNegative ? ['auto', 'auto'] : [0, 'auto']}
                                allowDecimals={current.unit !== 'int'}
                                tickFormatter={axisFormatter(current.unit)}
                                tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }}
                            />
                            <Tooltip
                                cursor={{ stroke: 'var(--color-gray-300)' }}
                                formatter={(v) => [format(typeof v === 'number' ? v : null), current.label]}
                                labelFormatter={(_, payload) => String(payload?.[0]?.payload?.label ?? '')}
                                contentStyle={{
                                    background: 'var(--color-white)',
                                    border: '1px solid var(--color-border-light)',
                                    borderRadius: 8,
                                    fontSize: 12,
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="var(--color-primary-600)"
                                strokeWidth={2.25}
                                dot={{ r: 4, fill: 'var(--color-primary-600)', strokeWidth: 0 }}
                                activeDot={{ r: 5 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <p className={styles.empty}>Sem dados suficientes para este período.</p>
                )}
            </div>
        </section>
    );
}
